import axios, { AxiosInstance } from "axios";
import { config } from "../config";
import {
  ZohoTicketSyncRequest,
  ZohoTicketSyncResponse,
  ZohoSyncJob,
  TicketSearchRequest,
  TicketSearchResponse,
  TicketStatsResponse,
  TicketCategory,
  TicketHealthStatus,
  TicketSearchResult,
} from "../types/tickets";

class TicketService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: config.API_URL,
    });

    // Add auth interceptor
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("access_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired, redirect to login
          localStorage.removeItem("access_token");
          window.location.href = "/";
        }
        return Promise.reject(error);
      }
    );
  }

  // Sync Management
  async startSync(
    request: ZohoTicketSyncRequest
  ): Promise<ZohoTicketSyncResponse> {
    const response = await this.api.post("/tickets/sync", request);
    return response.data;
  }

  async getSyncJobStatus(jobId: string): Promise<ZohoSyncJob> {
    const response = await this.api.get(`/tickets/sync/${jobId}/status`);
    return response.data;
  }

  async getRecentSyncJobs(
    limit: number = 10
  ): Promise<{ jobs: ZohoSyncJob[] }> {
    const response = await this.api.get(`/tickets/sync/jobs?limit=${limit}`);
    return response.data;
  }

  async cancelSyncJob(
    jobId: string
  ): Promise<{ message: string; job_id: string }> {
    const response = await this.api.post(`/tickets/sync/${jobId}/cancel`);
    return response.data;
  }

  // Search
  async searchTickets(
    request: TicketSearchRequest
  ): Promise<TicketSearchResponse> {
    const response = await this.api.post("/tickets/search", request);
    return response.data;
  }

  async getSimilarTickets(
    ticketId: string,
    limit: number = 5
  ): Promise<{ similar_tickets: TicketSearchResult[] }> {
    const response = await this.api.get(
      `/tickets/similar/${ticketId}?limit=${limit}`
    );
    return response.data;
  }

  // Statistics
  async getTicketStats(): Promise<TicketStatsResponse> {
    const response = await this.api.get("/tickets/stats");
    return response.data;
  }

  async getTicketCategories(): Promise<{ categories: TicketCategory[] }> {
    const response = await this.api.get("/tickets/categories");
    return response.data;
  }

  // Health
  async getHealthStatus(): Promise<TicketHealthStatus> {
    const response = await this.api.get("/tickets/health");
    return response.data;
  }
}

export const ticketService = new TicketService();
